<template>
  <div v-if="userLoaded && showKycBanner" class="kyc-banner">
    <p>
      ⚠️ Your account is unverified. Complete your KYC to continue using all
      features.
    </p>

    <button @click="kycModal = true" class="kyc-btn">
      Complete Verification
    </button>
  </div>

   <!-- KYC Modal -->
      <v-dialog v-model="kycModal" max-width="500" :eager="true">
        <v-card class="pa-4">
          <form @submit.prevent="submitKyc" class="w-full space-y-6">
            <h3 class="text-lg font-bold mb-4">KYC Verification</h3>

            <!-- BVN -->
            <div class="field mb-4">
              <label>BVN</label>
              <input v-model="kycForm.bvn" type="text" class="modern-input" />
            </div>

            <!-- BVN -->
            <div class="field mb-4">
              <label>Account Number</label>
              <input v-model="kycForm.acctNo" type="text" class="modern-input" />
            </div>

            <!--Bank-->
            <div class="field mb-4">
              <label>Bank</label>

              <v-autocomplete v-model="selectedBank" :items="banks || []" item-title="name" variant="outlined"
                return-object label="Select Bank" placeholder="Search bank..." clearable hide-no-data />
            </div>

            <!-- NIN -->
            <div class="field mb-4">
              <label>NIN</label>
              <input v-model="kycForm.nin" type="text" class="modern-input" />
            </div>

            <!-- TIN -->
            <!-- <div class="field mb-4">
              <label>TIN</label>
              <input v-model="kycForm.tin" type="text" class="modern-input" />
            </div> -->

            <div v-if="kycStatus" class="flex justify-center">
              <v-alert v-if="kycStatus" :type="kycStatus.type" density="compact" variant="tonal" class="text-center">
                {{ kycStatus.message }}
              </v-alert>
            </div>
            <v-card-actions class="justify-center gap-6 mt-4">

              <button @click="kycModal = false"
                class="px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition">
                Cancel
              </button>

              <button @click="submitKyc" :disabled="kycLoading"
                class="px-5 py-2 rounded-md bg-black text-white font-semibold transition-colors duration-300 hover:bg-green-600 flex items-center gap-2">
                <span>{{ kycLoading ? 'Verifying...' : 'Verify Me' }} </span>
                <v-icon v-if="kycLoading" class="spin"> mdi-loading</v-icon>
              </button>

            </v-card-actions>
          </form>
        </v-card>
      </v-dialog>
  
</template>

<script setup>
import { ref, computed, reactive, onMounted } from "vue";
import { useDashStore } from "@/store/dashboard";
import { performKyc } from "@/api/auth"
import { getAllErrors } from "@/util"
import { fetchBanks } from "@/api/dashboard"

//const auth = useAuthStore();
const dash = useDashStore();

const kycModal = ref(false);
const userLoaded = ref(false)
const kycLoading = ref(false)
const kycStatus = ref(null)
//const isUserLoaded = ref(false)
//const isUserLoaded = computed(() => !!dash.user?.user?.profile)

const firstName = computed(() => dash.user?.user?.profile?.firstname || "")
const lastName = computed(() => dash.user?.user?.profile?.lastname || "")

// const showKycBanner = computed(() => {
//     return auth.user && !auth.user.is_verified;
// });

const showKycBanner = computed(() => {
  const profile = dash.user?.user?.profile

  // User/profile hasn't loaded yet
  if (!profile) {
    return false
  }

  const status = String(profile.verification_status ?? '')
    .trim()
    .toLowerCase()

  return status !== 'completed'
})

onMounted(async () => {
  
   try {
    if (!dash.user?.user?.profile) {
      await dash.loadUser()
    }

    await loadBanks()
  } catch (error) {
    console.error('Failed to initialize GlobalKyc:', error)
  } finally {
    userLoaded.value = true
    kycLoading.value = false
  }
});

const kycForm = reactive({
  bvn: '',
  nin: '',
  tin: '',
  dob: ''
})

//Load banks for dropdown
const banks = ref([])
const selectedBank = ref(null)

const loadBanks = async () => {
  try {
    const res = await fetchBanks()

    // adjust depending on your API structure
    banks.value = res?.data?.data?.data || []
  } catch (e) {
    console.error(e)
    banks.value = []
  }
}

//Perform KYC submission

const submitKyc = async () => {
  //console.log("Selected bank:", selectedBank.value)

  kycLoading.value = true
  try {
    const res = await performKyc({
      bvn: kycForm.bvn,
      nin: kycForm.nin,
      //tin: kycForm.tin,
      firstname: firstName.value,
      lastname: lastName.value,
      email: dash.user?.user?.email,
      phone: dash.user?.user?.mobile_number,
      account_number: kycForm.acctNo,
      bank_code: selectedBank.value?.code,
      bank: selectedBank.value?.name
    })

    const accountName = res.data.data?.account_name || ''
    const accountNumber = res.data.data?.account_number || ''
    //const bankName = selectedBank.value?.name || ''

    kycStatus.value = {
      type: "success",
      message: `${accountName} - ${accountNumber} - Verified`
    }
    //kycModal.value = false
    await dash.loadUser() // refresh user data to get updated KYC status
  } catch (e) {
    const msg = getAllErrors(e)
    kycStatus.value = {
      type: "error",
      message: msg || "KYC verification failed"
    }
  } finally {
    kycLoading.value = false
  }
}
</script>

<style scoped>

/**Verifiction banner */
.kyc-banner {
  background: #fff3cd;
  border: 1px solid #ffeeba;
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.kyc-btn {
  background: #f59e0b;
  padding: 6px 12px;
  border-radius: 6px;
  color: white;
  font-weight: bold;
}

.modern-input {
  width: 100%;
  height: 46px;
  margin-top: 6px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  font-size: 15px;
  background: transparent;
  transition: all 0.2s ease;
}

.modern-input:focus {
  outline: none;
  border-color: #000;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
}
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>